import * as THREE from 'three';
import { attackSpec, DODGE_DURATION } from '../sim/attacks';
import { TICK_RATE } from '../sim/constants';
import type { Archetype, CharacterState } from '../sim/types';

// Articulated placeholder humanoid built from primitives, animated procedurally:
// walk/sprint cycles, light/heavy/kick swings, block pose, dodge lean, stagger,
// exhaustion slump, and a death fall. All joints ease toward per-frame targets
// so poses blend instead of snapping.

interface Palette {
  tunic: number;
  limbs: number;
  skin: number;
}

type PaletteKey =
  | CharacterState['kind']
  | 'soldier-friendly'
  | 'soldier-enemy'
  | 'agent-enemy'
  | 'king-enemy';

// Team colors are deliberately loud — battle readability at distance beats
// palette subtlety (review feedback: muddy colors made mid-range unreadable).
const PALETTES: Record<PaletteKey, Palette> = {
  agent: { tunic: 0x2b3a55, limbs: 0x222d42, skin: 0xd9b38c },
  king: { tunic: 0xc9a227, limbs: 0x9a7c1e, skin: 0xd9b38c },
  training: { tunic: 0x7a3b3b, limbs: 0x5e2e2e, skin: 0xc9a189 },
  soldier: { tunic: 0x3a6fe0, limbs: 0x2b52ad, skin: 0xd9b38c }, // fallback; teams use variants
  'soldier-friendly': { tunic: 0x3a6fe0, limbs: 0x2b52ad, skin: 0xd9b38c },
  'soldier-enemy': { tunic: 0xd93434, limbs: 0xa02626, skin: 0xc9a189 },
  'agent-enemy': { tunic: 0x6e222c, limbs: 0x521a20, skin: 0xc9a189 },
  'king-enemy': { tunic: 0xb8622a, limbs: 0x8a481e, skin: 0xc9a189 },
};

const EASE_RATE = 18; // 1/s — how fast joints approach their pose targets

/** A joint rotation target; every frame we set targets then ease toward them. */
class Joint {
  target = new THREE.Euler();
  constructor(public node: THREE.Object3D) {}

  set(x: number, y: number, z: number): void {
    this.target.set(x, y, z);
  }

  ease(dt: number): void {
    const k = Math.min(1, dt * EASE_RATE);
    this.node.rotation.x += (this.target.x - this.node.rotation.x) * k;
    this.node.rotation.y += (this.target.y - this.node.rotation.y) * k;
    this.node.rotation.z += (this.target.z - this.node.rotation.z) * k;
  }
}

function box(
  w: number,
  h: number,
  d: number,
  mat: THREE.MeshLambertMaterial,
  y: number
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.y = y;
  m.castShadow = true;
  return m;
}

export class CharacterRig {
  group = new THREE.Group();
  /** everything below tilts/falls together (dodge lean, death) */
  private body = new THREE.Group();
  private torsoPivot: Joint;
  private hipL: Joint;
  private hipR: Joint;
  private kneeL: Joint;
  private kneeR: Joint;
  private shoulderL: Joint;
  private shoulderR: Joint;
  private elbowR: Joint;
  private bodyJoint: Joint;
  private materials: THREE.MeshLambertMaterial[] = [];
  private meshes: THREE.Mesh[] = [];
  private walkPhase = 0;
  private lastX = 0;
  private lastZ = 0;
  private first = true;
  private shadows = true;
  private archetype: Archetype = 'infantry';

  constructor(
    kind: CharacterState['kind'],
    palette: PaletteKey = kind,
    archetype: Archetype = 'infantry'
  ) {
    const pal = PALETTES[palette];
    this.archetype = kind === 'soldier' ? archetype : 'infantry';
    const tunicMat = this.mat(pal.tunic);
    const limbMat = this.mat(pal.limbs);
    const skinMat = this.mat(pal.skin);

    this.group.add(this.body);
    this.bodyJoint = new Joint(this.body);

    // legs: hip pivot -> thigh, knee pivot -> shin
    const makeLeg = (side: number) => {
      const hip = new THREE.Group();
      hip.position.set(0.13 * side, 0.92, 0);
      hip.add(box(0.15, 0.44, 0.16, limbMat, -0.22));
      const knee = new THREE.Group();
      knee.position.y = -0.44;
      knee.add(box(0.13, 0.44, 0.14, limbMat, -0.22));
      hip.add(knee);
      this.body.add(hip);
      return { hip: new Joint(hip), knee: new Joint(knee) };
    };
    const legL = makeLeg(-1);
    const legR = makeLeg(1);
    this.hipL = legL.hip;
    this.kneeL = legL.knee;
    this.hipR = legR.hip;
    this.kneeR = legR.knee;

    // torso pivot at the waist
    const torsoPivot = new THREE.Group();
    torsoPivot.position.y = 0.95;
    this.body.add(torsoPivot);
    this.torsoPivot = new Joint(torsoPivot);
    torsoPivot.add(box(0.46, 0.62, 0.26, tunicMat, 0.31));

    // head + face marker
    const head = new THREE.Group();
    head.position.y = 0.85;
    const skull = new THREE.Mesh(new THREE.SphereGeometry(0.21, 12, 10), skinMat);
    skull.castShadow = true;
    head.add(skull);
    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.08, 0.12), this.mat(0x222222));
    visor.position.set(0, 0.03, 0.17);
    head.add(visor);
    if (kind === 'king') {
      const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.21, 0.16, 8), this.mat(0xf0d060));
      crown.position.y = 0.22;
      crown.castShadow = true;
      head.add(crown);
    } else if (kind === 'agent') {
      const hood = new THREE.Mesh(new THREE.ConeGeometry(0.26, 0.34, 8), this.mat(0x1c2638));
      hood.position.y = 0.18;
      head.add(hood);
    } else if (kind === 'soldier') {
      // team-colored kettle helmet: the head is ~30% of a unit's pixels, so
      // it carries team identity at range
      const helm = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.24, 0.13, 8), tunicMat);
      helm.position.y = 0.14;
      head.add(helm);
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.19, 8, 5), tunicMat);
      cap.position.y = 0.17;
      head.add(cap);
    }
    torsoPivot.add(head);

    // arms: shoulder pivot -> upper arm; right arm gets elbow + sword
    const makeArm = (side: number) => {
      const shoulder = new THREE.Group();
      shoulder.position.set(0.31 * side, 0.58, 0);
      shoulder.add(box(0.12, 0.34, 0.13, limbMat, -0.17));
      torsoPivot.add(shoulder);
      return shoulder;
    };
    const shoulderL = makeArm(-1);
    this.shoulderL = new Joint(shoulderL);

    const shoulderR = makeArm(1);
    this.shoulderR = new Joint(shoulderR);
    const elbow = new THREE.Group();
    elbow.position.y = -0.34;
    elbow.add(box(0.11, 0.3, 0.12, limbMat, -0.15));
    shoulderR.add(elbow);
    this.elbowR = new Joint(elbow);

    // sword in the right hand, pointing along local +Z
    const sword = new THREE.Group();
    sword.position.y = -0.32;
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.92), this.mat(0xb8bcc4));
    blade.position.z = 0.5;
    blade.castShadow = true;
    const guard = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.05, 0.05), this.mat(0x6b5a34));
    guard.position.z = 0.1;
    sword.add(blade, guard);
    elbow.add(sword);

    // archetype dressing for soldiers
    if (this.archetype === 'archer') {
      sword.visible = false;
      const bow = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.035, 5, 9, Math.PI), this.mat(0x6b5334));
      bow.rotation.z = Math.PI / 2;
      bow.position.y = -0.32;
      elbow.add(bow);
      const quiver = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.55, 6), this.mat(0x5a4632));
      quiver.position.set(-0.15, 0.35, -0.2);
      quiver.rotation.x = 0.3;
      torsoPivot.add(quiver);
    } else if (this.archetype === 'knight') {
      for (const side of [-1, 1]) {
        const pauldron = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.14, 0.28), this.mat(0x9aa0a8));
        pauldron.position.set(0.31 * side, 0.62, 0);
        torsoPivot.add(pauldron);
      }
      const helm = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.23, 0.2, 8), this.mat(0x9aa0a8));
      helm.position.y = 0.95;
      torsoPivot.add(helm);
    }

    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) this.meshes.push(o);
    });
  }

  private pennant: THREE.Group | null = null;

  /** Squad leaders carry a tall back-banner readable across the map; built
   * lazily on first promotion. */
  setPennant(on: boolean): void {
    if (on && !this.pennant) {
      this.pennant = new THREE.Group();
      const pole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.035, 0.035, 2.9, 5),
        this.mat(0x6b5334)
      );
      pole.position.y = 1.1;
      const flag = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.62, 0.85), this.mat(0xf0d060));
      flag.position.set(0, 2.2, 0.45);
      this.pennant.add(pole, flag);
      this.pennant.position.set(0, 0.6, -0.24);
      this.torsoPivot.node.add(this.pennant);
    }
    if (this.pennant) this.pennant.visible = on;
  }

  /** Toggle shadow casting (disabled for far-LOD rigs to cut shadow-pass cost). */
  setShadows(on: boolean): void {
    if (this.shadows === on) return;
    this.shadows = on;
    for (const m of this.meshes) m.castShadow = on;
  }

  private mat(color: number): THREE.MeshLambertMaterial {
    const m = new THREE.MeshLambertMaterial({ color });
    this.materials.push(m);
    return m;
  }

  setFlash(on: boolean): void {
    for (const m of this.materials) m.emissive.setHex(on ? 0x661414 : 0x000000);
  }

  /** Telegraph tint on the blade-arm during heavy windup (parry bait cue). */
  private joints(): Joint[] {
    return [
      this.bodyJoint,
      this.torsoPivot,
      this.hipL,
      this.hipR,
      this.kneeL,
      this.kneeR,
      this.shoulderL,
      this.shoulderR,
      this.elbowR,
    ];
  }

  update(
    c: CharacterState,
    x: number,
    y: number,
    z: number,
    heading: number,
    tick: number,
    dt: number,
    posePass = true
  ): void {
    this.group.position.set(x, y, z);
    this.group.rotation.y = heading;

    // planar velocity drives the walk cycle
    if (this.first) {
      this.lastX = x;
      this.lastZ = z;
      this.first = false;
    }
    const speed = dt > 0 ? Math.hypot(x - this.lastX, z - this.lastZ) / dt : 0;
    this.lastX = x;
    this.lastZ = z;

    // far-LOD rigs skip the pose pass most frames — position still updates
    if (!posePass) return;
    this.walkPhase += speed * dt * 3.4;

    if (!c.alive) {
      this.poseDead(c, tick);
    } else {
      this.poseAlive(c, speed);
    }

    for (const j of this.joints()) j.ease(dt);
  }

  private poseAlive(c: CharacterState, speed: number): void {
    const swing = Math.sin(this.walkPhase);
    const moveAmp = Math.min(0.65, speed * 0.12);

    // defaults: legs walk, arms swing, body upright
    this.bodyJoint.set(0, 0, 0);
    this.body.position.y = c.moving ? Math.abs(Math.sin(this.walkPhase)) * 0.04 : 0;
    this.hipL.set(swing * moveAmp, 0, 0);
    this.hipR.set(-swing * moveAmp, 0, 0);
    this.kneeL.set(Math.max(0, -swing) * moveAmp * 0.9, 0, 0);
    this.kneeR.set(Math.max(0, swing) * moveAmp * 0.9, 0, 0);
    this.shoulderL.set(-swing * moveAmp * 0.6 + 0.08, 0, 0.06);
    this.shoulderR.set(swing * moveAmp * 0.4 + 0.12, 0, -0.06);
    this.elbowR.set(-0.25, 0, 0);
    this.torsoPivot.set(c.exhausted ? 0.16 : 0.04, 0, 0);

    if (c.attacking && c.attackKind) {
      const spec = attackSpec(c.profile, c.attackKind);
      const duration = spec.windup + spec.recovery;
      const t = Math.min(1, c.attackElapsed / duration);
      const wf = spec.windup / duration;
      if (c.attackKind === 'kick') {
        // right leg snaps up, torso leans back
        const k = t < wf ? t / wf : 1 - (t - wf) / (1 - wf);
        this.hipR.set(-1.35 * k, 0, 0);
        this.kneeR.set(0.45 * k, 0, 0);
        this.torsoPivot.set(-0.22 * k, 0, 0);
      } else if (t < wf) {
        // windup: arm back and up, torso coiled; heavy goes overhead
        const k = t / wf;
        const overhead = c.attackKind === 'heavy';
        this.shoulderR.set(0.3 + k * (overhead ? 2.4 : 1.6), 0, -0.3 * k);
        this.elbowR.set(-0.5 - 0.4 * k, 0, 0);
        this.torsoPivot.set(-0.12 * k, 0.5 * k, 0);
      } else {
        // strike: sweep down-across, torso uncoils
        const k = Math.min(1, ((t - wf) / (1 - wf)) * 1.7);
        const overhead = c.attackKind === 'heavy';
        this.shoulderR.set((overhead ? 2.7 : 1.9) - k * (overhead ? 3.6 : 2.8), 0, -0.3 + 0.5 * k);
        this.elbowR.set(-0.9 + 0.7 * k, 0, 0);
        this.torsoPivot.set(-0.12 + 0.35 * k, 0.5 - 1.1 * k, 0);
      }
    } else if (c.blocking) {
      // sword held up across the body
      this.shoulderR.set(-1.5, 0, -0.5);
      this.elbowR.set(-1.1, 0.9, 0);
      this.shoulderL.set(-0.7, 0, 0.5);
      this.torsoPivot.set(0.1, -0.15, 0);
    }

    if (c.dodging) {
      // lean hard into the dodge direction (converted to local space)
      const rel = Math.atan2(c.dodgeDirX, c.dodgeDirZ) - c.heading;
      const k = Math.sin(Math.min(1, c.dodgeElapsed / DODGE_DURATION) * Math.PI);
      this.bodyJoint.set(Math.cos(rel) * 0.5 * k, 0, -Math.sin(rel) * 0.5 * k);
      this.body.position.y = -0.12 * k;
    }

    if (c.stagger > 0) {
      this.bodyJoint.set(-0.2, 0, 0);
      this.torsoPivot.set(-0.3, 0, 0);
      this.shoulderL.set(-0.5, 0, 0.7);
      this.shoulderR.set(-0.4, 0, -0.7);
    }
  }

  private poseDead(c: CharacterState, tick: number): void {
    const t = Math.min(1, (tick - c.lastHitTick) / (0.4 * TICK_RATE));
    this.bodyJoint.set(-(Math.PI / 2) * t, 0, 0);
    this.body.position.y = 0.1 * t;
    this.hipL.set(0.1, 0, 0);
    this.hipR.set(0.05, 0, 0);
    this.shoulderL.set(-0.3, 0, 0.4);
    this.shoulderR.set(-0.2, 0, -0.4);
    this.torsoPivot.set(0, 0, 0);
    this.kneeL.set(0.2, 0, 0);
    this.kneeR.set(0.3, 0, 0);
  }
}
